// Theme persistence. The active theme is an attribute on <html> (`data-theme`),
// which is the ONLY thing the token CSS branches on (styles/tokens/light.css).
// The stored choice wins; otherwise the system scheme. First paint is handled by
// the nonced inline script in layouts/_theme.html.erb - keep the two in sync.

export type Theme = 'dark' | 'light'

const STORAGE_KEY = 'reconforta-theme'

function readStored(): Theme | null {
  try {
    const value = localStorage.getItem(STORAGE_KEY)
    return value === 'light' || value === 'dark' ? value : null
  } catch {
    return null
  }
}

function systemTheme(): Theme {
  return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark'
}

export function resolveInitialTheme(): Theme {
  return readStored() ?? systemTheme()
}

export function applyTheme(theme: Theme): void {
  document.documentElement.dataset.theme = theme
}

// The system-change listener only steers visitors who never chose explicitly.
export function initTheme(): void {
  applyTheme(resolveInitialTheme())
  window.matchMedia('(prefers-color-scheme: light)').addEventListener('change', () => {
    if (readStored() === null) applyTheme(systemTheme())
  })
}

export function baseTheme(): Theme {
  return 'dark'
}

export function currentTheme(): Theme {
  const theme = document.documentElement.dataset.theme
  return theme === 'light' || theme === 'dark' ? theme : resolveInitialTheme()
}

export function subscribeTheme(onChange: () => void): () => void {
  const observer = new MutationObserver(onChange)
  observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] })
  return () => observer.disconnect()
}

export function setTheme(theme: Theme): void {
  try {
    localStorage.setItem(STORAGE_KEY, theme)
  } catch {
    // Private mode / disabled storage: theme still applies for this session.
  }
  applyTheme(theme)
}
